"use client";

import React from "react";
import { Phone, ShieldCheck, ArrowRight, CircleCheck as CheckCircle2, Mail } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const accreditations = [{
  name: "NASC",
  label: "National Access & Scaffolding Confederation"
}, {
  name: "CHAS",
  label: "Contractors Health & Safety Assessment"
}];
const highlights = ["20+ years director experience", "Commercial & residential", "NASC & CHAS verified"];
function HeroContent() {
  const shouldReduceMotion = useReducedMotion();
  const {
    posts,
    loading
  } = usePosts();
  const matched = posts?.find((p: any) => p.customFields?.photo_name === "Homepage Hero Image" || p.acf?.photo_name === "Homepage Hero Image");
  let imgSrc = "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/commercial_scaffolding_hero.png";
  let imgAlt = "Multi-tier commercial scaffolding structure on South East development site";
  if (matched) {
    const rawImg = matched.customFields?.image || (matched as any).acf?.image;
    if (typeof rawImg === "string" && rawImg.trim()) {
      imgSrc = rawImg;
    } else if (rawImg && typeof rawImg === "object") {
      if (typeof rawImg.url === "string" && rawImg.url.trim()) {
        imgSrc = rawImg.url;
      } else if (typeof rawImg.src === "string" && rawImg.src.trim()) {
        imgSrc = rawImg.src;
      }
    }
    const rawAlt = matched.customFields?.alt_text || (matched as any).acf?.alt_text;
    if (typeof rawAlt === "string" && rawAlt.trim()) {
      imgAlt = rawAlt.trim();
    }
  }
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: shouldReduceMotion ? 0 : 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 18
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="hero" data-nav="dark" className="dark relative min-h-[92vh] w-full overflow-hidden bg-background text-foreground flex items-center">
      {/* Background Image with Dark Scrim Overlay */}
      <div className="absolute inset-0 z-0">
        {loading ? <div className="h-full w-full bg-muted animate-pulse" /> : <img src={imgSrc} data-wvc-srcset={imgSrc === "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/commercial_scaffolding_hero.png" ? "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/commercial_scaffolding_hero.png-wvc-srcset" : ""} data-wvc-sizes={imgSrc === "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/commercial_scaffolding_hero.png" ? "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/commercial_scaffolding_hero.png-wvc-sizes" : ""} alt={imgAlt} loading="eager" decoding="async" fetchPriority="high" className="h-full w-full object-cover object-center" />}
        {/* Layered industrial scrim */}
        <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/75 to-background/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/45" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-24 sm:py-28 lg:py-32 w-full">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="max-w-3xl">
          {/* Structural Datum Rule & Eyebrow */}
          <motion.div variants={itemVariants} className="flex items-center gap-3 mb-6">
            <span className="h-[3px] w-10 bg-primary rounded-lg inline-block" />
            <span className="text-xs sm:text-sm font-bold tracking-wider uppercase text-primary font-default">
              Safety-led Scaffolding Contractor
            </span>
          </motion.div>

          {/* Primary Headline */}
          <motion.h1 variants={itemVariants} className="text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-foreground font-default leading-[1.1] mb-6">
            Commercial, Residential & Specialist Scaffolding
          </motion.h1>

          {/* Supporting Copy */}
          <motion.p variants={itemVariants} className="text-base sm:text-lg lg:text-xl text-muted-foreground leading-relaxed mb-8 max-w-2xl font-default">
            BMF Scaffolding Ltd provides commercial, residential and specialist scaffolding across Berkshire, Surrey, Hampshire, Buckinghamshire, Greater London and the wider South East. Led by Pete Wood's 20+ years of experience, we are safety-led and NASC and CHAS accredited.
          </motion.p>

          {/* Key Trust Checkpoints */}
          <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-y-2 gap-x-6 mb-10 text-xs sm:text-sm text-foreground/90 font-medium">
            {highlights.map((item, index) => <div key={item} data-index={index} className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>{item}</span>
              </div>)}
          </motion.div>

          {/* Action Trio Button Group with Direct Phone & Email */}
          <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-4 mb-14">
            <Button asChild size="lg" className="h-12 px-7 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-md transition-[color,background-color,box-shadow]">
              <Link to="/ContactRequestQuotePage" className="inline-flex items-center gap-2">
                Request a quote
                <ArrowRight className="size-4" />
              </Link>
            </Button>

            <Button asChild variant="secondary" size="lg" className="h-12 px-6 bg-secondary hover:bg-secondary/80 text-secondary-foreground font-medium border border-border transition-[color,background-color,border-color]">
              <a href="tel:01344929900" className="inline-flex items-center gap-2">
                <Phone className="size-4 text-primary" />
                Call 01344 929900
              </a>
            </Button>

            <Button asChild variant="secondary" size="lg" className="h-12 px-6 bg-secondary hover:bg-secondary/80 text-secondary-foreground font-medium border border-border transition-[color,background-color,border-color]">
              <a href="mailto:quotes@bmfscaffolding.co.uk" className="inline-flex items-center gap-2">
                <Mail className="size-4 text-primary" />
                quotes@bmfscaffolding.co.uk
              </a>
            </Button>

            <Button asChild variant="outline" size="lg" className="h-12 px-6 border-border hover:border-primary text-foreground hover:text-primary transition-[color,border-color,background-color]">
              <Link to="/ContactRequestQuotePage">
                Discuss a project
              </Link>
            </Button>
          </motion.div>

          {/* Verified Accreditation Strip */}
          <motion.div variants={itemVariants} className="pt-6 border-t border-border/80">
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">
              Confirmed Industry Accreditations
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
              {accreditations.map((acc, index) => <div key={acc.name} data-index={index} className="flex items-center gap-3 p-3 rounded-md bg-secondary/50 border border-border">
                  <div className="flex size-8 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <ShieldCheck className="size-4" />
                  </div>
                  <div>
                    <span className="block text-sm font-bold text-foreground leading-tight">
                      {acc.name}
                    </span>
                    <span className="block text-[11px] text-muted-foreground leading-tight">
                      {acc.label}
                    </span>
                  </div>
                </div>)}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}

// PERFORMANCE NOTE: This site's Lighthouse audit found ~9MB+ of avoidable image weight, primarily from Dynamic Content -> Site Photo uploads served as original files with no responsive/WebP variants (this is a WordPress media-processing limitation, not something fixable in this front-end code). Please check these homepage Site Photo records in wp-admin (Site Photos) and, for any that are large JPG/PNG originals, replace the uploaded image with a resized, compressed WebP file of the same crop: Homepage Hero Image (ID 500), Homepage Commercial Service Image (ID 493), Homepage Residential Service Image (ID 494), Homepage Roofing Service Image (ID 488), Homepage Solar Service Image (ID 492), Homepage Commercial Capability Image (ID 489), Homepage Roofing Contractor Image (ID 490), Homepage Solar Contractor Image (ID 498), Homepage Residential Showcase Image (ID 491), Homepage Family Story Image (ID 496). After replacing files, clear/regenerate the 10Web performance cache from the hosting dashboard and re-run Lighthouse.
export default function Hero() {
  const wp_query_params = {
    post_type: "site_photo",
    per_page: 100,
    status: ["publish"]
  };
  const wp_query = new WP_Query(wp_query_params);
  return <PostsProvider wp_query={{
    post_type: "site_photo",
    per_page: 100,
    status: ["publish"]
  }}>
      <HeroContent />
    </PostsProvider>;
}